import { useCallback, useEffect, useRef, useState } from "react";
import { buildEntitiesUrl, fetchEntities } from "./api";

const deriveHasMore = (nextCount, total, perPage) => {
  if (typeof total === "number") {
    return nextCount < total;
  }
  if (nextCount === 0) return false;
  return perPage > 0 && nextCount % perPage === 0;
};

const sanitizeSearch = (value = "") => value.trim();

export default function useEntityList({
  active = false,
  layerIds = [],
  taxonomyCodes = [],
  searchQuery = "",
  perPage = 40,
} = {}) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(null);

  const abortRef = useRef(null);
  const totalRef = useRef(null);
  const depsRef = useRef("");

  const filtersKey = JSON.stringify({
    layerIds: layerIds.slice().sort(),
    taxonomyCodes: taxonomyCodes.slice().sort(),
    searchQuery: sanitizeSearch(searchQuery),
    perPage,
  });

  const loadPage = useCallback(
    (targetPage = 1, { reset = false } = {}) => {
      if (!active) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (reset) {
        setItems([]);
        setHasMore(false);
        totalRef.current = null;
        setTotal(null);
      }

      setIsLoading(true);
      setError(null);

      const url = buildEntitiesUrl({
        layerIds,
        taxonomyCodes,
        q: sanitizeSearch(searchQuery) || undefined,
        perPage,
        fields: "lite",
        format: "json",
        page: targetPage,
      });

      fetchEntities(url, controller.signal)
        .then((payload) => {
          if (controller.signal.aborted) return;
          const rows = Array.isArray(payload?.data) ? payload.data : [];
          const total =
            payload?.meta?.total ??
            payload?.total ??
            (payload?.data && payload.data.total);

          totalRef.current = typeof total === "number" ? total : totalRef.current;
          setTotal(total ?? null);
          setItems((prev) => {
            const nextItems = reset ? rows : [...prev, ...rows];
            const knownTotal = totalRef.current;
            setHasMore(deriveHasMore(nextItems.length, knownTotal, perPage));
            return nextItems;
          });
          setPage(targetPage);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          setError(err?.message || "Unable to load list");
        })
        .finally(() => {
          if (controller.signal.aborted) return;
          setIsLoading(false);
        });
    },
    [active, layerIds, taxonomyCodes, searchQuery, perPage]
  );

  useEffect(() => {
    const hasChanged = depsRef.current !== filtersKey;
    if (!active) {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      return;
    }
    if (hasChanged) {
      depsRef.current = filtersKey;
      loadPage(1, { reset: true });
    } else if (items.length === 0 && !isLoading) {
      // First load when activating without dependency changes
      loadPage(1, { reset: true });
    }
  }, [active, filtersKey, loadPage, isLoading, items.length]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  const loadMore = useCallback(() => {
    if (!active || isLoading || !hasMore) return;
    loadPage(page + 1, { reset: false });
  }, [active, hasMore, isLoading, loadPage, page]);

  return {
    items,
    page,
    hasMore,
    isLoading,
    error,
    total,
    loadMore,
  };
}
